/**
 * Общие примитивы стола белота: локальный `ArmenianBelotTablePresentation` и онлайн-столы
 * импортируют отсюда, чтобы не расходились токены/геометрия взятки.
 *
 * Чеклист паритета локальный ↔ онлайн (живёт в коде, план не дублируем):
 * @see BELOT_LOCAL_ONLINE_PARITY_CHECKLIST
 */

import React, { useEffect, useRef } from 'react';
import { Braces, Eye, Timer } from 'lucide-react';
import { cn } from '../../poker-ui/utils/cn';
import { PlayingCard } from '../../poker-ui/PlayingCard';
import {
  belotCardColorClass,
  belotCardIsTrump,
  belotContractCpLabelFromEngine,
  belotMatchCpFromEnginePoints,
  belotMatchTargetCpFromEngine,
  formatBelotTrumpBid,
} from '../../game/belotArmenianCards';

/** Матрица паритета: P0 = Open 1×1 / оболочка; P1 = 4p полировка. */
export const BELOT_LOCAL_ONLINE_PARITY_CHECKLIST = Object.freeze([
  { id: 'shell', area: 'Оболочка', localRef: 'BelotArmenianLocalLobby', online: 'BaseGameRoomScreen', priority: 'P0' },
  { id: 'header', area: 'Шапка стола', localRef: 'GameIndustrialHeader', online: 'GameIndustrialHeader', priority: 'P0' },
  { id: 'hud', area: 'Счёт / взятки / раунд', localRef: 'ArmenianBelotTablePresentation', online: 'BelotHudMetricsRow', priority: 'P0' },
  { id: 'contract_bar', area: 'Полоса контракта', localRef: 'ArmenianBelotTablePresentation', online: 'BelotContractPlayBar', priority: 'P0' },
  { id: 'trick_slots', area: '4 слота взятки', localRef: 'TRICK_SLOT_TRANSFORM', online: 'BelotTrickFourSlots', priority: 'P0' },
  { id: 'auction', area: 'Торги', localRef: 'ArmenianAuctionPanel', online: 'ArmenianAuctionPanel', priority: 'P0' },
  { id: 'sound', area: 'Звук', localRef: 'useAudio + fx', online: 'useBelotOnlineFxBus', priority: 'P1' },
  { id: 'seat_anim', area: 'Анимации мест', localRef: 'gsap seatSlots', online: 'useBelotOnlineDealAnimation', priority: 'P1' },
  {
    id: 'play_drag_drop',
    area: 'Ход карты: drag в эллипс + snap-back',
    localRef: 'BelotArmenianLocalLobby (ellipse metric)',
    online: 'useBelotOnlineHeroDragPlay (OnlineOpenBelotTable, OnlineBelotTable)',
    priority: 'P0',
  },
  {
    id: 'play_card_flight',
    area: 'GSAP полёт карты, PLAY_CARD после завершения',
    localRef: 'useBelotArmenianAnimations',
    online: 'useBelotOnlinePlayCardFlight (open 1×1 и 4p)',
    priority: 'P0',
  },
  {
    id: 'trick_end_overlay',
    area: 'Короткий оверлей полной взятки (reveal)',
    localRef: 'useBelotTrickAnimations',
    online: 'useBelotOnlineTrickEndAnimation (open 1×1 и 4p)',
    priority: 'P1',
  },
]);

export const BELOT_TRICK_SLOT_TRANSFORM = {
  top: 'translate(-50%, -132%) rotate(2deg)',
  right: 'translate(44%, -50%) rotate(8deg)',
  bottom: 'translate(-50%, 30%) rotate(-2deg)',
  left: 'translate(-144%, -50%) rotate(-8deg)',
};

/** @deprecated use BELOT_TRICK_SLOT_TRANSFORM; оставлено для короткого импорта в презентации */
export const TRICK_SLOT_TRANSFORM = BELOT_TRICK_SLOT_TRANSFORM;

export function getRelativeSeatPosition(seatIndex, heroIndex) {
  if (seatIndex < 0 || heroIndex < 0) return 'top';
  const offset = (seatIndex - heroIndex + 4) % 4;
  if (offset === 0) return 'bottom';
  if (offset === 1) return 'left';
  if (offset === 2) return 'top';
  return 'right';
}

/** Эллипс рассадки 4 мест (герой внизу) — как в `OnlineBelotTable`. */
export const BELOT_ONLINE_SEAT_ELLIPSE_PRESETS = [
  { leftPct: 50, topPct: 84 },
  { leftPct: 8, topPct: 52 },
  { leftPct: 50, topPct: 20 },
  { leftPct: 92, topPct: 52 },
];

/** Узкий экран: «стопка» мест вокруг стола. */
export const BELOT_ONLINE_SEAT_MOBILE_STACK_PRESETS = [
  { leftPct: 50, topPct: 84 },
  { leftPct: 14, topPct: 40 },
  { leftPct: 50, topPct: 17 },
  { leftPct: 86, topPct: 40 },
];

/**
 * Только оболочка бейджа козыря (фон/рамка/свечение). Цвет символа — {@link belotTrumpGlyphFaceClass}.
 */
export function trumpBadgeToneByGlyph(glyph) {
  switch (glyph) {
    case '♥':
      return 'border-red-800/60 bg-red-950 shadow-[0_0_14px_rgba(185,28,28,0.42)]';
    case '♦':
      return 'border-amber-400/55 bg-amber-950 shadow-[0_0_14px_rgba(251,191,36,0.36)]';
    case '♠':
      return 'border-zinc-400/55 bg-zinc-950 shadow-[0_0_12px_rgba(255,255,255,0.14)]';
    case '♣':
      return 'border-emerald-500/55 bg-emerald-950 shadow-[0_0_14px_rgba(16,185,129,0.34)]';
    case 'БК':
      return 'border-violet-400/50 bg-violet-950 shadow-[0_0_16px_rgba(167,139,250,0.32)]';
    case 'AT':
      return 'border-yellow-400/50 bg-yellow-950 shadow-[0_0_16px_rgba(252,211,77,0.32)]';
    default:
      return 'border-white/25 bg-zinc-950';
  }
}

/** Капля торгов: непрозрачные заливки (пас / контра / реконтра / прочий ход). */
export const AUCTION_DROP_TONE_NEUTRAL = {
  bubble: cn(
    'border-2 border-white/25 bg-zinc-950 backdrop-blur-md',
    'shadow-[0_8px_32px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.1)]',
  ),
  tail: 'border-white/30 bg-zinc-950',
};

export const AUCTION_DROP_TONE_PASS = {
  bubble: cn(
    'border-2 border-red-400/65 bg-red-950 backdrop-blur-md',
    'shadow-[0_8px_32px_rgba(0,0,0,0.58),0_0_24px_rgba(220,38,38,0.35),inset_0_1px_0_rgba(255,255,255,0.1)]',
  ),
  tail: 'border-red-500/50 bg-red-950',
};

export const AUCTION_DROP_TONE_CONTRA = {
  bubble: cn(
    'border-2 border-amber-300/70 bg-amber-950 backdrop-blur-md',
    'shadow-[0_8px_32px_rgba(0,0,0,0.58),0_0_26px_rgba(245,158,11,0.38),inset_0_1px_0_rgba(255,255,255,0.1)]',
  ),
  tail: 'border-amber-300/70 bg-amber-950',
};

/** Реконтра — чуть светлее контры (`amber-900` vs `amber-950`), заливка непрозрачная. */
export const AUCTION_DROP_TONE_RECONTRA = {
  bubble: cn(
    'border-2 border-amber-200/75 bg-amber-900 backdrop-blur-md',
    'shadow-[0_8px_32px_rgba(0,0,0,0.55),0_0_26px_rgba(251,191,36,0.42),inset_0_1px_0_rgba(255,255,255,0.12)]',
  ),
  tail: 'border-amber-200/75 bg-amber-900',
};

/**
 * @param {'pass'|'contra'|'recontra'|'neutral'} kind
 * @returns {{ bubble: string, tail: string }}
 */
export function seatAuctionNonBidTonePair(kind) {
  if (kind === 'pass') return AUCTION_DROP_TONE_PASS;
  if (kind === 'contra') return AUCTION_DROP_TONE_CONTRA;
  if (kind === 'recontra') return AUCTION_DROP_TONE_RECONTRA;
  return AUCTION_DROP_TONE_NEUTRAL;
}

/**
 * Яркий контрастный цвет «фигурки» масти / подписи БК·AT на бейдже (поверх тёмной заливки).
 * @param {string} glyph — одна масть или «БК» / «AT» / «—»
 */
export function belotTrumpGlyphFaceClass(glyph) {
  const halo = '[text-shadow:0_1px_2px_rgba(0,0,0,0.95),0_0_14px_rgba(0,0,0,0.55)]';
  switch (glyph) {
    case '♥':
      return cn(
        'text-rose-200 drop-shadow-[0_0_16px_rgba(254,205,211,1)] saturate-150 contrast-[1.08]',
        halo,
      );
    case '♦':
      return cn(
        'text-amber-100 drop-shadow-[0_0_14px_rgba(253,230,138,1)] saturate-150 contrast-[1.08]',
        halo,
      );
    case '♣':
      return cn(
        'text-emerald-200 drop-shadow-[0_0_14px_rgba(167,243,208,1)] saturate-150 contrast-[1.08]',
        halo,
      );
    case '♠':
      return cn(
        'text-white drop-shadow-[0_0_18px_rgba(255,255,255,0.75)] contrast-[1.1]',
        '[text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_0_12px_rgba(255,255,255,0.35)]',
      );
    case 'БК':
      return cn('text-violet-100 drop-shadow-[0_0_14px_rgba(216,180,254,0.95)]', halo);
    case 'AT':
      return cn('text-yellow-100 drop-shadow-[0_0_14px_rgba(253,224,71,1)]', halo);
    default:
      return cn('text-slate-50 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]', halo);
  }
}

const BELOT_SUIT_SPLIT_RE = /([♣♦♥♠])/;

/**
 * Строка с числом и мастью: цифры нейтральные, символы мастей — яркие.
 * @param {{ text: string, neutralClassName?: string }} props
 */
/** Читаемость цифр/текста на зелёном сукне: светлый шрифт + тёмное свечение. */
export const BELOT_FELT_LABEL_NEUTRAL =
  'font-extrabold text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.92),0_0_18px_rgba(0,0,0,0.45)]';

export function BelotVividSuitInlineText({ text, neutralClassName = BELOT_FELT_LABEL_NEUTRAL }) {
  const s = String(text ?? '');
  const parts = s.split(BELOT_SUIT_SPLIT_RE);
  return (
    <>
      {parts.map((part, i) => {
        if (part === '♣' || part === '♦' || part === '♥' || part === '♠') {
          return (
            <span
              key={`belot-suit-${i}`}
              className={cn(
                belotTrumpGlyphFaceClass(part),
                'inline-block text-[1.18em] font-extrabold leading-none align-[-0.06em]',
              )}
            >
              {part}
            </span>
          );
        }
        if (part === '') return null;
        return (
          <span key={`belot-txt-${i}`} className={neutralClassName}>
            {part}
          </span>
        );
      })}
    </>
  );
}

/**
 * Капля торгов над местом (`SeatAuctionDropBadge`): фон и «хвост» по масти / режиму ставки.
 * Пики — монохром, черви — «кровавый» красный, бубны — янтарь, трефы — изумруд, БК / AT — как в панели.
 * @param {unknown} trump — 0..3 | 'no_trump' | 'all_trump'
 * @returns {{ bubble: string, tail: string }}
 */
export function seatAuctionBidTonePair(trump) {
  if (trump == null || trump === '') {
    return {
      bubble:
        'border-violet-300/70 bg-violet-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(139,92,246,0.42),inset_0_1px_0_rgba(255,255,255,0.13)]',
      tail: 'border-violet-300/70 bg-violet-950',
    };
  }
  const t = trump === 'no_trump' || trump === 'all_trump' ? trump : Number(trump);
  if (t === 'no_trump') {
    return {
      bubble:
        'border-violet-300/65 bg-violet-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(139,92,246,0.48),inset_0_1px_0_rgba(255,255,255,0.13)]',
      tail: 'border-violet-300/65 bg-violet-950',
    };
  }
  if (t === 'all_trump') {
    return {
      bubble:
        'border-yellow-300/70 bg-yellow-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(234,179,8,0.5),inset_0_1px_0_rgba(255,255,255,0.13)]',
      tail: 'border-yellow-300/70 bg-yellow-950',
    };
  }
  if (!Number.isFinite(t)) {
    return {
      bubble:
        'border-violet-300/70 bg-violet-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(139,92,246,0.42),inset_0_1px_0_rgba(255,255,255,0.13)]',
      tail: 'border-violet-300/70 bg-violet-950',
    };
  }
  switch (t) {
    case 2:
      return {
        bubble:
          'border-red-600/80 bg-red-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(127,29,29,0.6),inset_0_1px_0_rgba(255,255,255,0.13)]',
        tail: 'border-red-600/80 bg-red-950',
      };
    case 3:
      return {
        bubble:
          'border-zinc-200/55 bg-zinc-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(255,255,255,0.22),inset_0_1px_0_rgba(255,255,255,0.13)]',
        tail: 'border-zinc-200/55 bg-zinc-950',
      };
    case 1:
      return {
        bubble:
          'border-amber-300/75 bg-amber-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(245,158,11,0.48),inset_0_1px_0_rgba(255,255,255,0.13)]',
        tail: 'border-amber-300/75 bg-amber-950',
      };
    case 0:
      return {
        bubble:
          'border-emerald-300/75 bg-emerald-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(5,150,105,0.5),inset_0_1px_0_rgba(255,255,255,0.13)]',
        tail: 'border-emerald-300/75 bg-emerald-950',
      };
    default:
      return {
        bubble:
          'border-violet-300/70 bg-violet-950 shadow-[0_8px_32px_rgba(0,0,0,0.64),0_0_28px_rgba(139,92,246,0.42),inset_0_1px_0_rgba(255,255,255,0.13)]',
        tail: 'border-violet-300/70 bg-violet-950',
      };
  }
}

const TERZC_TONE = {
  bubble:
    'border-indigo-300/70 bg-indigo-950 shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_26px_rgba(129,140,248,0.42),inset_0_1px_0_rgba(255,255,255,0.11)]',
  tail: 'border-indigo-300/70 bg-indigo-950',
};

/**
 * Тон капли над местом в матче (комбо / терц / масть из текста) — та же палитра, что у ставки по масти.
 * @param {string} label
 * @returns {{ bubble: string, tail: string }}
 */
export function belotMatchDropTonePairFromLabel(label) {
  const s = String(label || '');
  if (/♣/.test(s)) return seatAuctionBidTonePair(0);
  if (/♦/.test(s)) return seatAuctionBidTonePair(1);
  if (/♥/.test(s)) return seatAuctionBidTonePair(2);
  if (/♠/.test(s)) return seatAuctionBidTonePair(3);
  if (/терц/i.test(s)) return TERZC_TONE;
  if (/капот|capot/i.test(s)) return seatAuctionBidTonePair('all_trump');
  if (/50|100|150|200|250/i.test(s) && /в\s*точк|точк/i.test(s)) return seatAuctionBidTonePair(null);
  return seatAuctionBidTonePair(null);
}

/**
 * Тон речевого пузыря Блот/Реблот по глифу козыря (вторая строка).
 * @param {string} line2
 * @returns {{ bubble: string, tail: string }}
 */
export function belotSpeechTonePairFromGlyphLine(line2) {
  const s = String(line2 || '').trim();
  const suitMap = { '♣': 0, '♦': 1, '♥': 2, '♠': 3 };
  for (const ch of s) {
    if (ch in suitMap) return seatAuctionBidTonePair(suitMap[/** @type {'♣'|'♦'|'♥'|'♠'} */ (ch)]);
  }
  if (/\bбк\b/i.test(s) || /без\s*козыр/i.test(s)) return seatAuctionBidTonePair('no_trump');
  if (/все\s*козыр/i.test(s) || /\bAT\b/i.test(s)) return seatAuctionBidTonePair('all_trump');
  return {
    bubble:
      'border-amber-200/70 bg-amber-950 shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_26px_rgba(245,158,11,0.35),inset_0_1px_0_rgba(255,255,255,0.11)]',
    tail: 'border-amber-200/70 bg-amber-950',
  };
}

export function largeTrumpDisplay(trump) {
  if (trump === 'all_trump') return { line1: 'AT', line2: 'все козыри', red: false };
  if (trump === 'no_trump') return { line1: 'БК', line2: 'без козыря', red: false };
  const n = Number(trump);
  if (Number.isInteger(n) && n >= 0 && n <= 3) {
    const sym = ['♣', '♦', '♥', '♠'][n];
    return { line1: sym, line2: null, red: n === 1 || n === 2 };
  }
  return { line1: '—', line2: null, red: false };
}

/**
 * Счёт / взятки / раунд — как полоса в `ArmenianBelotTablePresentation`.
 */
export function BelotHudMetricsRow({
  teamScores = [0, 0],
  tricksWonByTeam = [0, 0],
  roundIndex = 0,
  targetScore = 10010,
  children = null,
  className = '',
}) {
  const s0 = belotMatchCpFromEnginePoints(Number(teamScores[0]) || 0);
  const s1 = belotMatchCpFromEnginePoints(Number(teamScores[1]) || 0);
  const t0 = Number(tricksWonByTeam[0]) || 0;
  const t1 = Number(tricksWonByTeam[1]) || 0;
  const ri = Number(roundIndex) || 0;
  const ts = belotMatchTargetCpFromEngine(Number(targetScore) || 10010);
  return (
    <div
      className={cn(
        'mx-auto mb-6 flex max-w-2xl flex-col gap-2 border-b border-border/35 pb-4 sm:mb-7 sm:flex-row sm:items-center sm:justify-between sm:gap-3',
        className,
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground sm:text-xs">
        <span className="tabular-nums">
          <span className="text-muted-foreground/75">Счёт</span>{' '}
          <span className="font-mono font-semibold text-foreground">
            {s0} : {s1}
          </span>
          <span className="text-muted-foreground/65"> / {ts} CP</span>
        </span>
        <span className="text-muted-foreground/35">·</span>
        <span className="tabular-nums">
          <span className="text-muted-foreground/75">Взятки</span>{' '}
          <span className="font-mono font-semibold text-foreground">
            {t0} : {t1}
          </span>
        </span>
        <span className="text-muted-foreground/35">·</span>
        <span className="tabular-nums text-muted-foreground/90">
          Раунд <span className="font-mono font-medium text-foreground">{ri + 1}</span>
        </span>
      </div>
      {children ? (
        <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">{children}</div>
      ) : null}
    </div>
  );
}

/**
 * Полоса «Контракт» в фазе розыгрыша (как в локальной презентации).
 */
export function BelotContractPlayBar({
  contractTargetPoints,
  trump,
  kapot,
  contraMultiplier,
  contractTeamLabel,
  contractBadgeRef,
}) {
  const L = largeTrumpDisplay(trump);
  const cm = Number(contraMultiplier) || 1;
  const cpLabel = belotContractCpLabelFromEngine({ contractTargetPoints, kapot, tens: undefined });
  return (
    <div className="sticky top-0 z-[25] shrink-0 border-b border-border/50 bg-card/88 px-3 py-2 shadow-sm backdrop-blur-md">
      <div className="mx-auto flex max-w-2xl items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Контракт</p>
          <p className="mt-0.5 truncate text-xs font-medium leading-snug text-foreground sm:text-[13px]">
            <span className="font-mono tabular-nums text-amber-200/95">{cpLabel} CP</span>
            <span className="text-muted-foreground/80"> · </span>
            <span>{formatBelotTrumpBid(trump)}</span>
            {kapot ? (
              <>
                <span className="text-muted-foreground/80"> · </span>
                <span className="rounded border border-amber-500/35 bg-amber-950/20 px-1 py-px text-[10px] font-semibold uppercase text-amber-100/90">
                  капот
                </span>
              </>
            ) : null}
            {contractTeamLabel ? (
              <>
                <span className="text-muted-foreground/80"> · </span>
                <span className="text-muted-foreground">{contractTeamLabel}</span>
              </>
            ) : null}
            {cm > 1 ? <span className="ml-1 font-mono text-xs text-cyan-300/90">×{cm}</span> : null}
          </p>
        </div>
        <div
          ref={contractBadgeRef}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-black/30 text-xl font-bold leading-none shadow-inner sm:h-11 sm:w-11 sm:text-2xl',
            trumpBadgeToneByGlyph(L.line1),
          )}
          title={L.line2 || undefined}
          aria-hidden
        >
          <span className={belotTrumpGlyphFaceClass(L.line1)}>{L.line1}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Полоска торгов (как верхняя часть локальной презентации в bazar).
 * `badgeTrump` — значение масти/режима для `largeTrumpDisplay` (текущий лидирующий бид), иначе «—».
 */
export function BelotAuctionStickyBar({ headline, subline, badgeTrump, contractBadgeRef }) {
  const L = badgeTrump != null && badgeTrump !== '' ? largeTrumpDisplay(badgeTrump) : { line1: '—', line2: null, red: false };
  return (
    <div className="sticky top-0 z-[25] shrink-0 border-b border-border/50 bg-card/88 px-3 py-2 shadow-sm backdrop-blur-md">
      <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{headline}</p>
          <p className="mt-0.5 truncate text-xs font-medium leading-snug text-foreground sm:text-[13px]">{subline}</p>
        </div>
        <div
          ref={contractBadgeRef}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-black/30 text-xl font-bold leading-none shadow-inner sm:h-11 sm:w-11 sm:text-2xl',
            trumpBadgeToneByGlyph(L.line1),
          )}
          title={L.line2 || undefined}
          aria-hidden
        >
          <span className={belotTrumpGlyphFaceClass(L.line1)}>{L.line1}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Четыре слота взятки относительно героя (как оверлей в локальной презентации).
 * `heroSeatAnchor` — логическое место «низ стола» (0 или 1 в Open 1×1).
 */
export function BelotTrickFourSlots({
  plays,
  heroSeatAnchor,
  cardIdToPlaying,
  contractTrump,
  seatLabel,
  showEmptySlots = true,
}) {
  const bySeat = new Map();
  if (Array.isArray(plays)) {
    for (const p of plays) {
      if (p && Number.isInteger(p.seat) && (p.card === 0 || p.card)) {
        bySeat.set(p.seat, p.card);
      }
    }
  }
  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {[0, 1, 2, 3].map((seat) => {
        const card = bySeat.get(seat);
        const position = getRelativeSeatPosition(seat, heroSeatAnchor);
        const label = typeof seatLabel === 'function' ? seatLabel(seat) : `М${seat}`;
        return (
          <div
            key={`trick-slot-${seat}`}
            className="absolute left-1/2 top-1/2"
            style={{ transform: BELOT_TRICK_SLOT_TRANSFORM[position], zIndex: 10 + seat }}
          >
            <div className="relative flex flex-col items-center gap-0.5">
              {card != null && cardIdToPlaying ? (
                <PlayingCard
                  {...cardIdToPlaying(card)}
                  size="sm"
                  className={cn(
                    'shadow-2xl ring-1 ring-white/15',
                    belotCardIsTrump(card, contractTrump) ? belotCardColorClass(card, true) : '',
                  )}
                />
              ) : showEmptySlots ? (
                <div className="h-[52px] w-[38px] rounded-md border border-white/10 bg-black/25 shadow-inner sm:h-[54px] sm:w-[40px]" />
              ) : null}
              {card != null ? (
                <span className="max-w-[5rem] truncate whitespace-nowrap rounded bg-black/55 px-1 py-0.5 text-[9px] text-white/80">
                  {label}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Кнопки справа от HUD как в локале: листок, движок, взятка (с long-press), таймер.
 * `onOpenEngineDebug` — если не передан, кнопка «Движок» неактивна. Для открытого белота 1×1 онлайн передаётся из `BaseGameRoomScreen`.
 */
export function BelotHudToolbarButtons({
  onOpenScoreSheet,
  onOpenEngineDebug,
  onPeekLastTrick,
  canPeekLastTrick,
  heroSecLeft,
  heroTimedTurn,
}) {
  const lastTrickLongPressRef = useRef(null);
  const lastTrickLongPressTriggeredRef = useRef(false);

  const clearLastTrickLongPress = () => {
    if (lastTrickLongPressRef.current != null) {
      window.clearTimeout(lastTrickLongPressRef.current);
      lastTrickLongPressRef.current = null;
    }
  };

  useEffect(() => () => clearLastTrickLongPress(), []);

  return (
    <>
      {typeof onOpenScoreSheet === 'function' ? (
        <button
          type="button"
          onClick={onOpenScoreSheet}
          className="rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground transition hover:bg-muted/20 hover:text-foreground sm:text-[11px]"
        >
          Листок
        </button>
      ) : null}
      {typeof onOpenEngineDebug === 'function' ? (
        <button
          type="button"
          onClick={onOpenEngineDebug}
          className="inline-flex items-center gap-1 rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground transition hover:bg-muted/20 hover:text-foreground sm:text-[11px]"
        >
          <Braces className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
          Движок
        </button>
      ) : (
        <button
          type="button"
          disabled
          title="Движок доступен в локальном белоте"
          className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground/45 sm:text-[11px]"
        >
          <Braces className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
          Движок
        </button>
      )}
      {canPeekLastTrick && typeof onPeekLastTrick === 'function' ? (
        <button
          type="button"
          onClick={() => {
            if (lastTrickLongPressTriggeredRef.current) {
              lastTrickLongPressTriggeredRef.current = false;
              return;
            }
            onPeekLastTrick();
          }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            lastTrickLongPressTriggeredRef.current = false;
            clearLastTrickLongPress();
            lastTrickLongPressRef.current = window.setTimeout(() => {
              lastTrickLongPressRef.current = null;
              lastTrickLongPressTriggeredRef.current = true;
              onPeekLastTrick();
            }, 450);
          }}
          onPointerUp={clearLastTrickLongPress}
          onPointerLeave={clearLastTrickLongPress}
          onPointerCancel={clearLastTrickLongPress}
          className="inline-flex items-center gap-1 rounded-md border border-border/55 bg-transparent px-2 py-1 text-[10px] font-medium text-muted-foreground transition hover:bg-muted/20 hover:text-foreground sm:text-[11px]"
        >
          <Eye className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
          Взятка
        </button>
      ) : null}
      {heroSecLeft != null && heroTimedTurn ? (
        <span className="inline-flex items-center gap-1 rounded-md border border-neon-cyan/35 bg-neon-cyan/10 px-2 py-1 font-mono text-[10px] tabular-nums text-neon-cyan sm:text-[11px]">
          <Timer className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
          {heroSecLeft}s
        </span>
      ) : null}
    </>
  );
}
